import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowLeft, Clock, AlertCircle, FlaskConical, Gift } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';
import stripeService from '../../services/stripeService';
import currencyService from '../../services/currencyService';
import paymentMethodService from '../../services/paymentMethodService';
import PlanComparisonCard from './components/PlanComparisonCard';
import BillingHistoryItem from './components/BillingHistoryItem';

import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import ChangePlanModal from './components/ChangePlanModal';


import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import { isOwner } from '../../utils/rolePermissions';
import Icon from '../../components/AppIcon';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';



const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [usageData, setUsageData] = useState({});
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, trialEnd: null, daysRemaining: 0 });
  const [upgradeRecommendation, setUpgradeRecommendation] = useState({ shouldUpgrade: false, recommendedPlan: null, reason: '' });
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [testCheckoutLoading, setTestCheckoutLoading] = useState(false);
  const [testCheckoutResult, setTestCheckoutResult] = useState(null);
  
  // Refs for cleanup management
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    loadSubscriptionData();
    detectUserCurrency();
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const detectUserCurrency = async () => {
    const { currency } = await currencyService?.detectUserCurrency();
    setSelectedCurrency(currency);
  };

  const loadSubscriptionData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Force refresh subscription data from database (no caching)
      const { data: subscription, error: subError } = await subscriptionService?.getCurrentSubscription();
      if (subError) throw subError;
      
      console.log('Loaded subscription:', subscription); // Debug log
      setCurrentSubscription(subscription);

      // Load trial status
      const trialInfo = await subscriptionService?.isUserInTrial();
      setTrialStatus(trialInfo);

      // Load upgrade recommendation
      const recommendation = await subscriptionService?.getUpgradeRecommendation();
      setUpgradeRecommendation(recommendation);

      // Load available plans
      const { data: plans, error: plansError } = await subscriptionService?.getAvailablePlans();
      if (plansError) throw plansError;
      setAvailablePlans(plans || []);

      // Load billing history
      const { data: history, error: historyError } = await subscriptionService?.getBillingHistory(10);
      if (historyError) throw historyError;
      setBillingHistory(history || []);

      // Load usage data for limited features
      const exportUsage = await subscriptionService?.getFeatureUsage('monthly_exports');
      const caregiverUsage = await subscriptionService?.getFeatureUsage('caregiver_connections');
      
      setUsageData({
        monthlyExports: exportUsage,
        caregiverConnections: caregiverUsage
      });
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planType, billingInterval = 'monthly') => {
    // Clear any existing timeout first
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef?.current);
      timeoutRef.current = null;
    }

    setCheckoutLoading(true);
    setError(null);

    // Timeout protection with proper cleanup
    timeoutRef.current = setTimeout(() => {
      if (isMountedRef?.current) {
        setCheckoutLoading(false);
        setError('Checkout session timed out. Please try again or contact support at support@mybodytoday1.com');
        alert('The checkout process took too long. Please try again or contact support.');
      }
      timeoutRef.current = null;
    }, 30000);

    try {
      // Validate plan type
      const validPlanTypes = ['basic', 'premium'];
      if (!validPlanTypes?.includes(planType)) {
        throw new Error(`Invalid plan type: ${planType}`);
      }

      // Validate billing interval
      const validBillingIntervals = ['monthly', 'yearly'];
      if (!validBillingIntervals?.includes(billingInterval)) {
        throw new Error(`Invalid billing interval: ${billingInterval}`);
      }

      // Validate payment method for currency
      if (!paymentMethodService?.isMethodSupportedForCurrency(selectedPaymentMethod, selectedCurrency)) {
        throw new Error(`${selectedPaymentMethod} is not supported for ${selectedCurrency}`);
      }

      console.log('🚀 Starting checkout process for:', { planType, billingInterval });

      // Create Stripe checkout session
      const { data, error: checkoutError } = await stripeService?.createCheckoutSession(
        planType,
        billingInterval
      );

      if (checkoutError) {
        console.error('❌ Checkout error:', checkoutError);
        
        // Check for specific configuration errors
        if (checkoutError?.message?.includes('not configured') || checkoutError?.code === 'STRIPE_NOT_CONFIGURED') {
          throw new Error('Payment system is currently unavailable. Please contact support at support@mybodytoday1.com or call 1-800-555-0123.');
        }
        
        throw checkoutError;
      }

      // Validate checkout URL
      if (!data?.url || typeof data?.url !== 'string') {
        console.error('❌ Invalid checkout URL:', data);
        throw new Error('Invalid checkout URL received. Please contact support.');
      }

      // Clear timeout before redirect (CRITICAL FIX)
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }

      // Redirect to Stripe checkout
      console.log('✅ Redirecting to Stripe checkout:', data?.url);
      window.location.href = data?.url;
      
      // Note: Component will unmount during redirect, loading state persists intentionally
    } catch (err) {
      console.error('❌ Upgrade failed:', err);
      
      // Clear timeout on error
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }
      
      // Only update state if component is still mounted
      if (isMountedRef?.current) {
        const errorMessage = err?.message || 'Failed to start checkout process. Please try again or contact support.';
        setError(errorMessage);
        setCheckoutLoading(false);
        
        // Show user-friendly alert with support contact
        alert(`Unable to process subscription upgrade:\n\n${errorMessage}\n\nIf this issue persists, please contact:\n📧 support@mybodytoday1.com\n📞 1-800-555-0123`);
      }
    }
  };

  const handleCancelSubscription = async (cancellationData) => {
    setActionLoading(true);
    setError(null);
    
    try {
      // Open Stripe Customer Portal for cancellation
      const { data, error: portalError } = await stripeService?.createPortalSession();
      
      if (portalError) {
        throw portalError;
      }

      if (!data?.url || typeof data?.url !== 'string') {
        throw new Error('Invalid portal URL received from server');
      }

      // Store cancellation feedback before redirecting
      if (cancellationData?.reason || cancellationData?.feedback) {
        localStorage.setItem('cancellation_feedback', JSON.stringify(cancellationData));
      }
      
      console.log('Redirecting to Stripe portal:', data?.url);
      window.location.href = data?.url;
    } catch (err) {
      console.error('Cancel subscription failed:', err);
      
      if (isMountedRef?.current) {
        const errorMessage = err?.message || 'Failed to open subscription management portal';
        setError(errorMessage);
        setActionLoading(false);
        setShowCancelModal(false);
        alert(`Cancellation failed: ${errorMessage}`);
      }
    }
  };

  const handleChangePlan = async (planType, billingInterval) => {
    setShowChangePlanModal(false);
    setActionLoading(false);
    
    // Use the main upgrade handler which has proper error handling
    await handleUpgrade(planType, billingInterval);
  };

  const handleTestCheckout = async () => {
    setTestCheckoutLoading(true);
    setTestCheckoutResult(null);
    try {
      const { data, error: checkoutError } = await stripeService?.createCheckoutSession('premium', 'monthly');
      if (checkoutError) throw checkoutError;
      if (!data?.url) throw new Error('No checkout URL returned');
      // Redirect to Stripe checkout
      window.location.href = data?.url;
    } catch (err) {
      setTestCheckoutResult({ success: false, message: err?.message || 'Checkout failed' });
      setTestCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <X className="w-12 h-12 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Subscription</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadSubscriptionData}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const currentPlan = currentSubscription?.plan;
  const isBasicPlan = currentPlan?.planType === 'basic';
  const isBasicUltraPlan = currentPlan?.planType === 'basic_ultra';
  const isOwnerAccount = isOwner(user);
  
  // Check if user is a MyBodyToday officer
  const isOfficer = user?.email?.toLowerCase()?.includes('@mybodytoday.') ||
    user?.email?.toLowerCase()?.includes('@mybodytoday1.') ||
    user?.email?.toLowerCase() === 'owner@mybodytoday.com' ||
    user?.email?.toLowerCase() === 'admin@mybodytoday.com' ||
    user?.email?.toLowerCase() === 'owner@mybodytoday1.com' ||
    user?.email?.toLowerCase() === 'admin@mybodytoday1.com' ||
    user?.user_metadata?.is_officer === true;
  
  // Check if user has an active paid subscription (not basic/free, not trial)
  const hasPaidSubscription = !isBasicPlan && !trialStatus?.inTrial && currentSubscription?.status === 'active';

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Checkout Loading Overlay - Enhanced with better UX */}
      {checkoutLoading && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          style={{ pointerEvents: 'all' }}
        >
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Processing Your Request</h3>
              <p className="text-gray-600 mb-4">Please wait while we prepare your checkout session...</p>
              <p className="text-sm text-gray-500">You'll be redirected to Stripe in a moment.</p>
              <p className="text-xs text-gray-400 mt-4">This may take up to 30 seconds</p>
            </div>
          </div>
        </div>
      )}
      <UserProfileHeader />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" className="w-4 h-4 text-gray-600" />
              </button>
              <div>
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Subscription Management</h1>
                <p className="text-xs sm:text-sm md:text-base text-gray-600">Manage your plan and billing</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LogoutButton showLabel={false} ariaLabel="Sign out from subscription management" />
              <CloseButton navigateTo="/dashboard" ariaLabel="Close subscription management" onClose={() => navigate('/dashboard')} />
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trial Status Banner */}
        {trialStatus?.inTrial && trialStatus?.daysRemaining <= 7 && trialStatus?.daysRemaining > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-orange-900 mb-1">
                Trial ends in {trialStatus?.daysRemaining} day{trialStatus?.daysRemaining !== 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-orange-800">
                Upgrade now to keep your data and continue tracking your health journey.
              </p>
            </div>
          </div>
        )}

        {/* Upgrade Recommendation Banner - Hidden for Owner */}
        {!isOwnerAccount && upgradeRecommendation?.shouldUpgrade && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-1">Upgrade Recommended</h3>
              <p className="text-sm text-amber-800 mb-3">Unlock doctor consultation summaries, patient notes, trigger tracking, and appointment reminders.</p>
              <button
                onClick={() => handleUpgrade('premium', 'monthly')}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
              >
                Upgrade to Premium — £4.99/month
              </button>
            </div>
          </div>
        )}

        {/* Current Plan Status */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {isBasicPlan ? 'Free Forever' : currentPlan?.name}
              </h2>
              <p className="text-gray-600 mt-1">
                {isBasicPlan ? 'Track your symptoms — free forever' : currentPlan?.description}
              </p>
              {trialStatus?.inTrial && (
                <div className="mt-2 inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4" />
                  Trial: {trialStatus?.daysRemaining} days left
                </div>
              )}
            </div>
            <div className="text-right">
              {isBasicPlan ? (
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                  {trialStatus?.inTrial ? 'Free Trial' : 'Free Forever'}
                </div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-gray-900">
                    ${currentSubscription?.billingInterval === 'yearly' 
                      ? currentPlan?.priceYearly 
                      : currentPlan?.priceMonthly}
                  </div>
                  <div className="text-sm text-gray-600">
                    per {currentSubscription?.billingInterval === 'yearly' ? 'year' : 'month'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subscription Details */}
          {!isBasicPlan && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-semibold text-gray-900 capitalize">{currentSubscription?.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Period</p>
                <p className="font-semibold text-gray-900">
                  {new Date(currentSubscription?.currentPeriodStart)?.toLocaleDateString()} - {new Date(currentSubscription?.currentPeriodEnd)?.toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Next Billing Date</p>
                <p className="font-semibold text-gray-900">
                  {new Date(currentSubscription?.currentPeriodEnd)?.toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {/* Subscription Actions - Show for all paid plans (basic_ultra, premium, family) */}
          {hasPaidSubscription && (
            <div className="mt-6 pt-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowChangePlanModal(true)}
                disabled={actionLoading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Loading...' : 'Change Plan'}
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={actionLoading}
                className="flex-1 px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Loading...' : 'Cancel Subscription'}
              </button>
            </div>
          )}
        </div>

        {/* Test Checkout Section */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <FlaskConical className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-900 text-lg">Stripe Test Checkout</h3>
              <p className="text-sm text-amber-800 mt-1">
                Verify the £4.99/month Premium charge processes correctly through Stripe. Use Stripe test card <code className="bg-amber-100 px-1 rounded font-mono text-xs">4242 4242 4242 4242</code> with any future expiry and CVC.
              </p>
            </div>
          </div>
          {testCheckoutResult && (
            <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
              testCheckoutResult?.success
                ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
            }`}>
              {testCheckoutResult?.success ? '✅ ' : '❌ '}{testCheckoutResult?.message}
            </div>
          )}
          {isOfficer && (
            <button
              onClick={handleTestCheckout}
              disabled={testCheckoutLoading}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FlaskConical className="w-4 h-4" />
              {testCheckoutLoading ? 'Initiating...' : 'Test £4.99 Checkout'}
            </button>
          )}
          <p className="text-xs text-amber-700 mt-2">After payment, receipt will show "MyBodyToday Premium — £4.99/month"</p>
        </div>

        {/* Referral Banner — shown above plan cards */}
        {isBasicPlan && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">💚</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-green-900 mb-1">Earn Premium Free — Refer Friends!</h3>
                <p className="text-green-800 text-sm mb-3">
                  Refer 5 friends → Unlock Doctor Consultation Summary.<br />
                  Refer 10 friends → Unlock Full Premium (worth £4.99/month).<br />
                  <span className="font-semibold">No payment needed.</span>
                </p>
                <button
                  onClick={() => navigate('/referral-program')}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
                >
                  <Gift className="w-4 h-4" />
                  Start Referring
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Comparison — only show Free and Premium */}
        {isBasicPlan && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Upgrade Your Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
              {availablePlans
                ?.filter((plan) => ['basic', 'premium']?.includes(plan?.planType))
                ?.map((plan) => (
                  <PlanComparisonCard
                    key={plan?.id}
                    plan={plan}
                    currentPlan={currentPlan}
                    onUpgrade={handleUpgrade}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Feature Comparison Matrix — 2 plans only */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Feature</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">Free Forever</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">Premium — £4.99/mo</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Symptom Tracking</td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Symptom History</td>
                  <td className="text-center py-3 px-4 text-gray-600">Unlimited</td>
                  <td className="text-center py-3 px-4 text-gray-600">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Monthly Exports</td>
                  <td className="text-center py-3 px-4 text-gray-600">2</td>
                  <td className="text-center py-3 px-4 text-gray-600">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Doctor Consultation Summary</td>
                  <td className="text-center py-3 px-4"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Patient Notes &amp; Trigger Tracking</td>
                  <td className="text-center py-3 px-4"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Doctor Appointment Reminders</td>
                  <td className="text-center py-3 px-4"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 text-gray-700">Advanced Analytics</td>
                  <td className="text-center py-3 px-4"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-700">Priority Support</td>
                  <td className="text-center py-3 px-4"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Billing History */}
        {!isBasicPlan && billingHistory?.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Billing History</h2>
            <div className="space-y-3">
              {billingHistory?.map((record) => (
                <BillingHistoryItem key={record?.id} record={record} />
              ))}
            </div>
            <p className="mt-4 text-xs text-gray-500 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              Payments are processed securely by{' '}
              <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Stripe</a>
              . We never store your full card details. See our{' '}
              <a href="/privacy-policy" className="underline hover:text-gray-700">Privacy Policy</a>
              {' '}for more information.
            </p>
          </div>
        )}
      </div>
      {/* Modals */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        currentPlan={currentPlan}
        loading={actionLoading}
      />
      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        onConfirm={handleChangePlan}
        currentPlan={currentPlan}
        availablePlans={availablePlans}
        loading={actionLoading}
      />
    </div>
  );
};

export default SubscriptionManagement;